import {
	closestCenter,
	pointerWithin,
	type CollisionDetection,
} from "@dnd-kit/core";

import {
	statusTabAtPointerFromBar,
	TASK_BOARD_STATUSES,
	type TaskBoardStatus,
} from "./task-board-order.js";

/** Set during mobile board drag so collision ignores hidden columns. */
export const taskBoardCompactDrag = {
	active: false,
	mobileColumn: "new" as TaskBoardStatus,
};

function isColumnDropId(id: string): boolean {
	return id.startsWith("task-col:") || id.startsWith("task-col-top:");
}

/** Latest pointer position from dnd-kit collision passes (viewport coordinates). */
export const taskBoardPointer = { x: 0, y: 0 };

/** Prefer pointer hits on cards; fall back to column/top slots, then center. */
export const taskBoardCollision: CollisionDetection = (args) => {
	if (args.pointerCoordinates) {
		taskBoardPointer.x = args.pointerCoordinates.x;
		taskBoardPointer.y = args.pointerCoordinates.y;
	}

	const pointerY = args.pointerCoordinates?.y ?? taskBoardPointer.y;
	const pointerX = args.pointerCoordinates?.x ?? taskBoardPointer.x;
	if (typeof document !== "undefined" && Number.isFinite(pointerX) && Number.isFinite(pointerY)) {
		const tabStatus = statusTabAtPointerFromBar(pointerX, pointerY);
		if (tabStatus) {
			return [{ id: `task-col:${tabStatus}` }];
		}
	}
	if (typeof document !== "undefined" && Number.isFinite(pointerY)) {
		const columnScan: readonly TaskBoardStatus[] = taskBoardCompactDrag.active
			? [taskBoardCompactDrag.mobileColumn]
			: TASK_BOARD_STATUSES;
		for (const status of columnScan) {
			const col = document.querySelector(`[data-task-col="${status}"]`);
			const scrollBody = col?.querySelector(".task-col-body");
			if (!(col instanceof HTMLElement) || !(scrollBody instanceof HTMLElement)) continue;
			const bounds = col.getBoundingClientRect();
			if (pointerX < bounds.left - 12 || pointerX > bounds.right + 12) continue;
			const bodyRect = scrollBody.getBoundingClientRect();
			if (pointerY <= bodyRect.top + 40) {
				return [{ id: `task-col-top:${status}` }];
			}
		}
	}

	const pointerHits = pointerWithin(args);
	if (pointerHits.length > 0) {
		const tops = pointerHits.filter((hit) => String(hit.id).startsWith("task-col-top:"));
		if (tops.length > 0) {
			const pointerY = args.pointerCoordinates?.y ?? 0;
			const nearestTop = tops.sort((left, right) => {
				const a = args.droppableRects.get(left.id);
				const b = args.droppableRects.get(right.id);
				if (!a || !b) return 0;
				return Math.abs(pointerY - (a.top + a.height / 2)) - Math.abs(pointerY - (b.top + b.height / 2));
			})[0];
			if (nearestTop) return [nearestTop];
		}
		const cards = pointerHits.filter((hit) => !isColumnDropId(String(hit.id)));
		if (cards.length > 0) {
			const sorted = cards.sort((left, right) => {
				const a = args.droppableRects.get(left.id);
				const b = args.droppableRects.get(right.id);
				if (!a || !b) return 0;
				const pointerY = args.pointerCoordinates?.y ?? 0;
				const distA = Math.abs(pointerY - (a.top + a.height / 2));
				const distB = Math.abs(pointerY - (b.top + b.height / 2));
				return distA - distB;
			});
			const columnGap = pointerHits.find((hit) => {
				const id = String(hit.id);
				return id.startsWith("task-col:") && !id.startsWith("task-col-top:");
			});
			if (columnGap) return [columnGap, ...sorted];
			return sorted;
		}
		return pointerHits;
	}
	return closestCenter(args);
};
